import slugify from 'slugify';
import { transliterate } from 'transliteration';
import { Cache } from '../cache';
import type { Article, ArticleIndex, ArticleIndexCompressed } from './types';

export class BlogClient {
  private _key: string;
  private _baseUrl: string;
  private _indexCache: ArticleIndex[] | null = null;
  private _indexPromise: Promise<ArticleIndex[]> | null = null;
  private _lastFetchTime: number = 0;
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes
  private _articleCache: Cache<Article>;

  constructor(key: string) {
    if (!key) {
      throw Error('SEObot API key must be provided');
    }
    this._key = key;
    this._baseUrl = 'https://cdn.seobotai.com';
    this._articleCache = new Cache<Article>(300);
  }

  private _decompressIndex(short: ArticleIndexCompressed): ArticleIndex {
    return {
      id: short.id,
      slug: short.s,
      headline: short.h,
      metaDescription: short.d,
      image: short.i,
      readingTime: short.rt,
      createdAt: short.cr,
      updatedAt: short.up,
      category: short.c
        ? {
            title: short.c.t,
            slug: short.c.s || slugify(transliterate(short.c.t), { lower: true, strict: true }),
          }
        : null,
      tags: (short.tg || []).map(i => ({
        title: i.t,
        slug: i.s || slugify(transliterate(i.t), { lower: true, strict: true }),
      })),
    };
  }

  private async _fetchIndex(): Promise<ArticleIndex[]> {
    // If we have a cached index and it's still fresh, return it
    if (this._indexCache && Date.now() - this._lastFetchTime < this.CACHE_TTL) {
      return this._indexCache;
    }

    // If there's already a fetch in progress, return that promise
    if (this._indexPromise) {
      return this._indexPromise;
    }

    // Start a new fetch
    this._indexPromise = (async () => {
      try {
        const response = await fetch(`${this._baseUrl}/${this._key}/system/base.json`);
        
        if (!response.ok) {
          console.error('Failed to fetch index:', {
            status: response.status,
            statusText: response.statusText,
            url: response.url
          });
          throw new Error(`Failed to fetch index: ${response.statusText}`);
        }

        const data = await response.json() as ArticleIndexCompressed[];
        const articles = data
          .map(i => this._decompressIndex(i))
          .sort((a, b) => b.createdAt.localeCompare(a.createdAt));

        // Update cache
        this._indexCache = articles;
        this._lastFetchTime = Date.now();
        return articles;
      } finally {
        this._indexPromise = null;
      }
    })();

    return this._indexPromise;
  }

  private async _fetchArticle(id: string): Promise<Article> {
    const article = await this._articleCache.get(id, async () => {
      const response = await fetch(`${this._baseUrl}/${this._key}/blog/${id}.json`);
      if (!response.ok) {
        throw new Error(`Failed to fetch article: ${response.statusText}`);
      }
      return response.json() as Promise<Article>;
    });

    if (!article) {
      throw new Error(`Failed to fetch article: ${id}`);
    }

    return article;
  }

  public async getArticles(page: number = 1, limit: number = 10): Promise<{ articles: ArticleIndex[]; total: number }> {
    const articles = await this._fetchIndex();
    const start = (page - 1) * limit;
    const end = start + limit;
    return {
      articles: articles.slice(start, end),
      total: articles.length
    };
  }

  public async getCategoryArticles(slug: string, page: number = 1, limit: number = 10): Promise<{ articles: ArticleIndex[]; total: number }> {
    const articles = await this._fetchIndex();
    const filtered = articles.filter(article => article.category?.slug === slug);
    const start = (page - 1) * limit;
    const end = start + limit;
    return {
      articles: filtered.slice(start, end),
      total: filtered.length
    };
  }

  public async getTagArticles(slug: string, page: number = 1, limit: number = 10): Promise<{ articles: ArticleIndex[]; total: number }> {
    const articles = await this._fetchIndex();
    const filtered = articles.filter(article => article.tags.some(tag => tag.slug === slug));
    const start = (page - 1) * limit;
    const end = start + limit;
    return {
      articles: filtered.slice(start, end),
      total: filtered.length
    };
  }

  public async getArticle(slug: string): Promise<Article> {
    const articles = await this._fetchIndex();
    const article = articles.find(a => a.slug === slug);
    if (!article) {
      throw new Error(`Article not found: ${slug}`);
    }
    return this._fetchArticle(article.id);
  }
} 